import { useEffect, useMemo, useState } from 'react'
import { Deal, User, Buyer, TransportProvider, SaleMethod } from '../../types'
import {
  loadTransportProviders,
  providersForState,
  stateFromLocation,
  checkSaleMargin,
  saveProposal,
  attachBuyerToDeal,
  checkInvoiceReadiness,
} from '../../services/saleService'
import { searchBuyers, upsertBuyer, isValidAbn, lookupAbn, CREDIT_STATUS_LABEL, AbrResult } from '../../services/buyerService'
/* Winnie, 15 Sep: "[Buyer] fields should similarly check against Contacts, so a repeat
   wholesale buyer doesn't need re-entering every deal." */
import { Contact, contactsService } from '../../services/contactsService'
import ContactSavePrompt from '../ContactSavePrompt'
import { toast } from '../Toast'
import './SalePanel.css'

/**
 * Creating a sale (§5.1–5.3).
 *
 * One screen rather than a proposal screen and a sale screen, because they are
 * the same facts at two levels of commitment: price the vehicle, pick who is
 * carrying it, say where it is going. "Save proposal" writes it down;
 * "Confirm sale" is what actually moves the deal.
 *
 * The margin is recalculated as you type and it is measured after transport and
 * the buying fee — a sale that clears on the sticker and loses money once it has
 * been freighted interstate is precisely what the warning is for.
 */

interface Props {
  deal: Deal
  user: User
  onSaved: (message: string) => void
  onConfirmSale: () => void
}

const money = (n: number | null | undefined) =>
  n === null || n === undefined
    ? '—'
    : `$${Number(n).toLocaleString('en-AU', { maximumFractionDigits: 0 })}`

const SALE_METHODS: { value: SaleMethod; label: string }[] = [
  { value: 'wholesale', label: 'Wholesale' },
  { value: 'auction', label: 'Auction' },
  { value: 'retail', label: 'Retail' },
  { value: 'trade', label: 'Trade' },
]

export default function SalePanel({ deal, user, onSaved, onConfirmSale }: Props) {
  const [providers, setProviders] = useState<TransportProvider[]>([])
  const [buyer, setBuyer] = useState<Buyer | null>(null)
  const [buyerTerm, setBuyerTerm] = useState('')
  const [buyerHits, setBuyerHits] = useState<Buyer[]>([])
  /* Contacts that are not yet buyer records. Searched alongside the buyers table rather than
     instead of it: a buyer record carries credit status, risk rating and the LMCT the tax
     invoice needs, none of which a contact has, so a contact is a head start on the form and
     not a substitute for the record. */
  const [contactHits, setContactHits] = useState<Contact[]>([])
  const [showNewBuyer, setShowNewBuyer] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [abrNote, setAbrNote] = useState<string | null>(null)
  const [abrResult, setAbrResult] = useState<AbrResult | null>(null)

  const [form, setForm] = useState({
    salePrice: deal.sale_price != null ? String(deal.sale_price) : '',
    saleMethod: (deal.sale_method || 'wholesale') as SaleMethod,
    orderDate: deal.order_date || new Date().toISOString().slice(0, 10),
    saleDate: deal.sale_date || new Date().toISOString().slice(0, 10),
    deliveryAddress: deal.delivery_address || '',
    transportArrangedBy: (deal.transport_arranged_by || 'direct_auto') as 'direct_auto' | 'dealer',
    transportProviderId: deal.transport_provider_id || '',
    transportCost: deal.transport_cost != null ? String(deal.transport_cost) : '',
    saleOdometer: deal.sale_odometer != null ? String(deal.sale_odometer) : '',
    saleRego: deal.sale_rego || '',
    financierName: deal.buyer_financier_name || '',
    financierContact: deal.buyer_financier_contact || '',
    financierReference: deal.buyer_financier_reference || '',
    financierPayout: deal.buyer_financier_payout != null ? String(deal.buyer_financier_payout) : '',
  })

  const [newBuyer, setNewBuyer] = useState({
    name: '', legal_name: '', trading_name: '', abn: '', lmct: '', email: '', mobile: '',
    billing_address: '', delivery_address: '',
  })

  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }))

  useEffect(() => {
    loadTransportProviders().then(setProviders).catch(() => setProviders([]))
  }, [])

  useEffect(() => {
    if (!deal.buyer_id) return
    searchBuyers(deal.buyer_name || '', 1)
      .then((hits) => setBuyer(hits.find((b) => b.id === deal.buyer_id) || null))
      .catch(() => {})
  }, [deal.buyer_id, deal.buyer_name])

  useEffect(() => {
    let cancelled = false
    if (buyerTerm.trim().length < 2) {
      setBuyerHits([])
      return
    }
    const t = window.setTimeout(() => {
      searchBuyers(buyerTerm)
        .then((hits) => !cancelled && setBuyerHits(hits))
        .catch(() => !cancelled && setBuyerHits([]))
      contactsService
        .searchByType('Buyer', buyerTerm)
        .then((hits) => !cancelled && setContactHits(hits))
        .catch(() => !cancelled && setContactHits([]))
    }, 250)
    return () => {
      cancelled = true
      window.clearTimeout(t)
    }
  }, [buyerTerm])

  // The delivery state decides the transport panel, falling back to where the
  // vehicle currently is when no delivery address has been typed yet.
  const deliveryState = useMemo(
    () => stateFromLocation(form.deliveryAddress) || stateFromLocation(deal.seller_location),
    [form.deliveryAddress, deal.seller_location]
  )

  const panel = useMemo(
    () => providersForState(providers, deliveryState),
    [providers, deliveryState]
  )

  const margin = useMemo(
    () =>
      checkSaleMargin(
        deal,
        parseFloat(form.salePrice) || 0,
        parseFloat(form.transportCost) || 0,
        form.transportArrangedBy
      ),
    [deal, form.salePrice, form.transportCost, form.transportArrangedBy]
  )

  const readiness = useMemo(() => checkInvoiceReadiness(deal, buyer), [deal, buyer])

  const pickBuyer = async (b: Buyer) => {
    setError(null)
    setBusy(true)
    try {
      await attachBuyerToDeal(deal.id, b.id)
      setBuyer(b)
      setBuyerTerm('')
      setBuyerHits([])
      setContactHits([])
      if (!form.deliveryAddress && b.delivery_address) set('deliveryAddress', b.delivery_address)
      toast.saved()
      onSaved(`${b.name} attached to this sale.`)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not attach that buyer')
    } finally {
      setBusy(false)
    }
  }

  const createBuyer = async () => {
    setError(null)
    if (!newBuyer.name.trim()) return setError('The buyer needs a name.')
    if (!newBuyer.email.trim()) return setError('The buyer needs an email — the invoice goes there.')
    if (!newBuyer.lmct.trim()) {
      return setError('An LMCT/MDL licence is compulsory on a wholesale tax invoice.')
    }
    if (newBuyer.abn && !isValidAbn(newBuyer.abn)) {
      return setError('That ABN does not pass the checksum — one of the digits is wrong.')
    }
    setBusy(true)
    try {
      const saved = await upsertBuyer(newBuyer)
      await attachBuyerToDeal(deal.id, saved.id)
      /* The buyer joins the Contacts registry too, so the next deal with them does not start
         from a blank form. Never awaited into the failure path — see contactsService. */
      void contactsService.matchOrCreate({
        name: saved.name,
        legal_name: saved.legal_name || null,
        abn: saved.abn || null,
        email: saved.email || null,
        phone: saved.mobile || saved.phone || null,
        address: saved.billing_address || saved.delivery_address || null,
        contact_type: ['Buyer'],
      })
      setBuyer(saved)
      setShowNewBuyer(false)
      toast.saved()
      onSaved(`${saved.name} created and attached.`)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save that buyer')
    } finally {
      setBusy(false)
    }
  }

  const persist = async (): Promise<boolean> => {
    setError(null)
    const price = parseFloat(form.salePrice)
    if (!price || price <= 0) {
      setError('Enter the sale price.')
      return false
    }
    setBusy(true)
    try {
      await saveProposal(
        deal,
        {
          buyerId: buyer?.id || deal.buyer_id || null,
          salePrice: price,
          saleMethod: form.saleMethod,
          orderDate: form.orderDate || null,
          saleDate: form.saleDate || null,
          deliveryAddress: form.deliveryAddress || null,
          saleOdometer: form.saleOdometer.trim() ? Number(form.saleOdometer) : null,
          saleRego: form.saleRego.trim() || null,
          buyerFinancierName: form.financierName.trim() || null,
          buyerFinancierContact: form.financierContact.trim() || null,
          buyerFinancierReference: form.financierReference.trim() || null,
          buyerFinancierPayout: form.financierPayout.trim() ? Number(form.financierPayout) : null,
          transportProviderId: form.transportProviderId || null,
          transportArrangedBy: form.transportArrangedBy,
          transportCost: parseFloat(form.transportCost) || 0,
        },
        user.id
      )
      return true
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the proposal')
      return false
    } finally {
      setBusy(false)
    }
  }

  const handleProposal = async () => {
    if (await persist()) {
      toast.saved()
      onSaved('Sale proposal saved.')
    }
  }

  const handleConfirm = async () => {
    if (!buyer && !deal.buyer_id) {
      setError('Attach a buyer before confirming the sale.')
      return
    }
    if (await persist()) onConfirmSale()
  }

  return (
    <div className="sale-panel">
      <div className="sale-head">
        <h4>Sale</h4>
        <p>
          Price the vehicle, choose who carries it and where it is going. Nothing moves until you
          confirm the sale — and a confirmed sale can be reversed for seven days.
        </p>
      </div>

      {/* ---------------------------------------------- buyer ---- */}
      <section className="sale-section">
        <h5>Buyer</h5>

        {buyer ? (
          <div className="sale-buyer">
            <div>
              <strong>{buyer.name}</strong>
              {buyer.trading_name && <span className="sale-muted"> trading as {buyer.trading_name}</span>}
              <div className="sale-muted">
                {buyer.abn ? `ABN ${buyer.abn}` : 'No ABN'} ·{' '}
                {buyer.lmct ? `LMCT ${buyer.lmct}` : <em className="sale-warn">no LMCT on file</em>} ·{' '}
                {buyer.email || 'no email'}
              </div>
            </div>
            <div className="sale-buyer-flags">
              {buyer.credit_status !== 'ok' && (
                <span className={`sale-chip credit-${buyer.credit_status}`}>
                  {CREDIT_STATUS_LABEL[buyer.credit_status]}
                </span>
              )}
              <span className={`sale-chip risk-${buyer.risk_rating}`}>
                {buyer.risk_rating === 'unrated' ? 'Not yet rated' : `${buyer.risk_rating} risk`}
              </span>
              <button className="sale-link" onClick={() => setBuyer(null)}>Change</button>
            </div>
          </div>
        ) : (
          <>
            <input
              className="sale-input"
              placeholder="Search by name, trading name, ABN or email…"
              value={buyerTerm}
              onChange={(e) => setBuyerTerm(e.target.value)}
            />
            {buyerHits.length > 0 && (
              <ul className="sale-hits">
                {buyerHits.map((b) => (
                  <li key={b.id}>
                    <button onClick={() => pickBuyer(b)} disabled={busy}>
                      <strong>{b.name}</strong>
                      <span className="sale-muted">
                        {b.abn ? ` · ABN ${b.abn}` : ''}
                        {b.lmct ? ` · LMCT ${b.lmct}` : ''}
                        {b.credit_status !== 'ok' ? ` · ${CREDIT_STATUS_LABEL[b.credit_status]}` : ''}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {/* Winnie, 15 Sep: the buyer fields check Contacts too, so a repeat wholesale
                buyer is not re-entered every deal. A contact is not itself a buyer record —
                it carries no LMCT, credit status or risk rating — so picking one opens the
                new-buyer form with everything it does know already filled in. */}
            {contactHits.filter((c) => !buyerHits.some((b) => (b.abn || '') === (c.abn || '') && !!c.abn)).length > 0 && (
              <>
                <div className="sale-muted" style={{ marginTop: 8, fontSize: 12 }}>In Contacts</div>
                <ul className="sale-hits">
                  {contactHits
                    .filter((c) => !buyerHits.some((b) => (b.abn || '') === (c.abn || '') && !!c.abn))
                    .map((c) => (
                      <li key={c.id}>
                        <button
                          disabled={busy}
                          onClick={() => {
                            setNewBuyer((n) => ({
                              ...n,
                              name: c.name || n.name,
                              legal_name: c.legal_name || n.legal_name,
                              abn: c.abn || n.abn,
                              email: c.email || n.email,
                              mobile: c.phone || n.mobile,
                              billing_address: c.address || n.billing_address,
                            }))
                            setShowNewBuyer(true)
                          }}
                        >
                          <strong>{c.name}</strong>
                          <span className="sale-muted">
                            {c.contact_number ? ` · ${c.contact_number}` : ''}
                            {c.abn ? ` · ABN ${c.abn}` : ''}
                            {' · needs an LMCT before it can be invoiced'}
                          </span>
                        </button>
                      </li>
                    ))}
                </ul>
              </>
            )}

            <button className="sale-link" onClick={() => setShowNewBuyer((v) => !v)}>
              {showNewBuyer ? 'Cancel' : '+ New buyer'}
            </button>

            {showNewBuyer && (
              <div className="sale-grid">
                {([
                  ['name', 'Name *'],
                  ['trading_name', 'Trading name'],
                  ['legal_name', 'Legal name'],
                  ['abn', 'ABN'],
                  ['lmct', 'LMCT / MDL *'],
                  ['email', 'Email *'],
                  ['mobile', 'Mobile'],
                  ['billing_address', 'Billing address'],
                  ['delivery_address', 'Delivery address'],
                ] as [keyof typeof newBuyer, string][]).map(([k, label]) => (
                  <div className="sale-field" key={k}>
                    <label htmlFor={`nb-${k}`}>{label}</label>
                    <input
                      id={`nb-${k}`}
                      value={newBuyer[k]}
                      onChange={(e) => setNewBuyer((n) => ({ ...n, [k]: e.target.value }))}
                    />
                  </div>
                ))}
                <div className="sale-field wide">
                  <div className="sale-actions">
                    <button
                      className="sale-btn"
                      disabled={busy || !newBuyer.abn}
                      onClick={async () => {
                        setAbrNote(null)
                        setBusy(true)
                        const r = await lookupAbn(newBuyer.abn)
                        setBusy(false)
                        setAbrResult(r)
                        setAbrNote(r.message)
                        if (r.ok) {
                          setNewBuyer((n) => ({
                            ...n,
                            legal_name: r.legalName || n.legal_name,
                            name: n.name || r.legalName || '',
                            trading_name: n.trading_name || (r.tradingNames || [])[0] || '',
                          }))
                        }
                      }}
                    >
                      Look up ABN
                    </button>
                    <button className="sale-btn primary" onClick={createBuyer} disabled={busy}>
                      Create buyer
                    </button>
                  </div>
                  {abrNote && <small className="sale-hint">{abrNote}</small>}
                  <ContactSavePrompt
                    abn={newBuyer.abn}
                    result={abrResult}
                    type="Buyer"
                    suggest={{
                      name: newBuyer.name,
                      email: newBuyer.email,
                      phone: newBuyer.mobile,
                      address: newBuyer.billing_address,
                    }}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </section>

      {/* ------------------------------------------------ sale ---- */}
      <section className="sale-section">
        <h5>Sale</h5>
        <div className="sale-grid">
          <div className="sale-field">
            <label htmlFor="sale-price">Sale price</label>
            <input
              id="sale-price"
              type="number"
              value={form.salePrice}
              onChange={(e) => set('salePrice', e.target.value)}
            />
          </div>
          <div className="sale-field">
            <label htmlFor="sale-method">Sale method</label>
            <select id="sale-method" value={form.saleMethod} onChange={(e) => set('saleMethod', e.target.value)}>
              {SALE_METHODS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
          <div className="sale-field">
            <label htmlFor="sale-order-date">Order date</label>
            <input id="sale-order-date" type="date" value={form.orderDate} onChange={(e) => set('orderDate', e.target.value)} />
          </div>
          <div className="sale-field">
            <label htmlFor="sale-date">Sale date</label>
            <input id="sale-date" type="date" value={form.saleDate} onChange={(e) => set('saleDate', e.target.value)} />
          </div>
          <div className="sale-field wide">
            <label htmlFor="sale-delivery">Delivery address</label>
            <input
              id="sale-delivery"
              value={form.deliveryAddress}
              onChange={(e) => set('deliveryAddress', e.target.value)}
              placeholder="Where the vehicle is going — the state here picks the transport panel"
            />
          </div>
          <div className="sale-field">
            <label htmlFor="sale-odometer">Odometer at sale</label>
            <input
              id="sale-odometer"
              inputMode="numeric"
              value={form.saleOdometer}
              onChange={(e) => set('saleOdometer', e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="Reading today, not at purchase"
            />
          </div>
          <div className="sale-field">
            <label htmlFor="sale-rego">Rego at sale</label>
            <input
              id="sale-rego"
              value={form.saleRego}
              onChange={(e) => set('saleRego', e.target.value.toUpperCase())}
              placeholder="Blank if unregistered"
            />
          </div>
          <div className="sale-field">
            <label htmlFor="sale-financier">Buyer&rsquo;s financier</label>
            <input
              id="sale-financier"
              value={form.financierName}
              onChange={(e) => set('financierName', e.target.value)}
              placeholder="Leave blank if the buyer is paying us directly"
            />
          </div>
          <div className="sale-field">
            <label htmlFor="sale-financier-contact">Financier contact</label>
            <input
              id="sale-financier-contact"
              value={form.financierContact}
              onChange={(e) => set('financierContact', e.target.value)}
              placeholder="Who to chase for the settlement"
            />
          </div>
          <div className="sale-field">
            <label htmlFor="sale-financier-ref">Financier reference</label>
            <input
              id="sale-financier-ref"
              value={form.financierReference}
              onChange={(e) => set('financierReference', e.target.value)}
              placeholder="Their settlement or approval number"
            />
          </div>
          <div className="sale-field">
            <label htmlFor="sale-financier-payout">Financier payout</label>
            <input
              id="sale-financier-payout"
              inputMode="decimal"
              value={form.financierPayout}
              onChange={(e) => set('financierPayout', e.target.value.replace(/[^0-9.]/g, ''))}
              placeholder="Amount the financier is settling"
            />
          </div>
        </div>
      </section>

      {/* ------------------------------------------- transport ---- */}
      <section className="sale-section">
        <h5>
          Transport
          {deliveryState && <span className="sale-muted"> · panel for {deliveryState}</span>}
        </h5>

        <div className="sale-radio-row">
          <label>
            <input
              type="radio"
              checked={form.transportArrangedBy === 'direct_auto'}
              onChange={() => set('transportArrangedBy', 'direct_auto')}
            />
            Direct Auto arranges it
          </label>
          <label>
            <input
              type="radio"
              checked={form.transportArrangedBy === 'dealer'}
              onChange={() => set('transportArrangedBy', 'dealer')}
            />
            Buyer arranges their own — no cost added
          </label>
        </div>

        {form.transportArrangedBy === 'direct_auto' && (
          <div className="sale-grid">
            <div className="sale-field">
              <label htmlFor="sale-carrier">Carrier</label>
              <select
                id="sale-carrier"
                value={form.transportProviderId}
                onChange={(e) => set('transportProviderId', e.target.value)}
              >
                <option value="">Select a carrier…</option>
                {panel.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} — {p.scope === 'national' ? 'national' : (p.states || []).join('/')}
                  </option>
                ))}
              </select>
              {panel.length === 0 && (
                <small className="sale-hint">
                  No carrier on the panel covers {deliveryState || 'this state'}. Add one under Admin,
                  or have the buyer arrange transport.
                </small>
              )}
            </div>
            <div className="sale-field">
              <label htmlFor="sale-transport-cost">Quoted cost</label>
              <input
                id="sale-transport-cost"
                type="number"
                value={form.transportCost}
                onChange={(e) => set('transportCost', e.target.value)}
              />
            </div>
          </div>
        )}
      </section>

      {/* ---------------------------------------------- margin ---- */}
      <div className={`sale-margin${margin.warning ? ' warn' : ''}`}>
        <div>
          <span className="sale-margin-label">Net margin after costs</span>
          <strong className="sale-margin-value">{money(margin.netMargin)}</strong>
        </div>
        <div className="sale-margin-detail">
          Guide wants {money(margin.guideTarget)} at a {money(deal.valuation_amount)} buy-in.
          {margin.reason && <> {margin.reason}</>}
        </div>
      </div>

      {!readiness.ok && (
        <div className="sale-gate">
          <strong>Before a tax invoice can be issued:</strong>
          <ul>
            {readiness.problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      )}

      {error && <div className="sale-error">{error}</div>}

      <div className="sale-actions">
        <button className="sale-btn" onClick={handleProposal} disabled={busy}>
          {deal.proposal_created_at ? 'Update proposal' : 'Create proposal'}
        </button>
        <button className="sale-btn primary" onClick={handleConfirm} disabled={busy}>
          Confirm sale
        </button>
      </div>
    </div>
  )
}
